import Color from 'color';
import { createContext, useContext } from 'react';
import { hasWindow } from '@quicktools/shared-components/utils/window';

import { generatePaletteFromUrl, generatePalettes } from '../utils/common/generatePalettes';

import { IStateModel, ActionTypesEnum, IActionModel, ExportStatusEnum } from './types';

const searchParams = new URLSearchParams(hasWindow ? window.location.search : undefined);
const colors = searchParams.get('colors');
const colorsFromUrl = colors?.split('-').map((item) => item && new Color(`#${item}`)) || [];
const hasUrlColors = colorsFromUrl.length != 0;
const palettesCount = colorsFromUrl.length || 5;
const palettes = hasUrlColors
  ? generatePaletteFromUrl(colorsFromUrl as Color<string>[])
  : generatePalettes(palettesCount, null);

export const initialState: IStateModel = {
  hasUrlColors,
  palettes,
  paletteSettings: {
    hue: 0,
    saturation: 0,
    temperature: 0,
    brightness: 0,
  },
  appState: [
    {
      palettes,
    },
  ],
  appStateVersion: 0,
  exportStatus: ExportStatusEnum.READY_TO_START,
};

export const reducer = (state: IStateModel, { type, payload }: IActionModel): IStateModel => {
  switch (type) {
    case ActionTypesEnum.SET_EXPORT_STATUS:
      return {
        ...state,
        exportStatus: payload,
      };
    case ActionTypesEnum.SET_PALETTES:
      return {
        ...state,
        palettes: payload,
      };
    case ActionTypesEnum.SET_PALETTE_SETTINGS:
      return {
        ...state,
        paletteSettings: payload,
      };
    case ActionTypesEnum.SET_SIDEBAR_STATE:
      return {
        ...state,
        sidebarState: payload,
      };
    case ActionTypesEnum.ADD_APP_STATE:
      if (state.appStateVersion + 1 === state.appState.length) {
        return {
          ...state,
          appState: [...state.appState, { palettes: state.palettes }],
          appStateVersion: state.appStateVersion + 1,
        };
      } else {
        return {
          ...state,
          appState: [
            ...state.appState.slice(0, state.appStateVersion + 1),
            { palettes: state.palettes },
          ],
          appStateVersion: state.appStateVersion + 1,
        };
      }
    case ActionTypesEnum.GO_PREV_VERSION:
      const prevVersion = state.appStateVersion - 1;

      return {
        ...state,
        ...state.appState[prevVersion],
        appStateVersion: prevVersion,
      };
    case ActionTypesEnum.GO_NEXT_VERSION:
      const nextVersion = state.appStateVersion + 1;

      return {
        ...state,
        ...state.appState[nextVersion],
        appStateVersion: nextVersion,
      };
    default:
      return state;
  }
};

export const Store = createContext({
  state: initialState,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  dispatch: (args: any) => {},
});

export const useStore = () => useContext(Store);
